/**
 * Where a role applies (the whole business, or chosen websites / domain folders) and the effective
 * access preview shown before saving a role change or sending an invitation. Identity decides:
 * the preview is its dry run (`POST …/access-preview`), and saving is checked again.
 */
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { Minus, Plus } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router";

import { useOrganization } from "@/auth/organization";
import { Alert, cx, ErrorAlert, Spinner } from "@/components/ui";
import {
  accessErrorMessage,
  emptySelections,
  groupChanges,
  narrowableMap,
  narrowableTypes,
  type ResourceNames,
  type ScopeDraft,
} from "@/lib/access";
import { identity, portal } from "@/lib/api";
import { domainsApi } from "@/lib/domains";
import type { AccessChange, ResourceType, RestrictionsByRole, Role, RoleRestrictions } from "@/lib/types";

import { permissionLabel } from "./roles";

/** The business's websites and domain folders to choose from, and Identity's permission catalogue. */
export function useAccessResources(enabled = true) {
  const { orgId } = useOrganization();
  const sites = useQuery({ queryKey: ["sites", orgId], queryFn: () => portal.sites(orgId), enabled });
  const folders = useQuery({
    queryKey: ["domains", orgId, "folders"],
    queryFn: () => domainsApi.folders(orgId),
    enabled,
  });
  const catalogue = useQuery({
    queryKey: ["permission-catalogue"],
    queryFn: identity.permissionCatalogue,
    staleTime: Infinity,
    enabled,
  });
  return useMemo(() => {
    const siteList = (sites.data?.sites ?? []).map((s) => ({ id: s.id, name: s.name }));
    const folderList = (folders.data?.folders ?? []).map((f) => ({ id: f.id, name: f.name }));
    const names: ResourceNames = {
      site: new Map(siteList.map((s) => [s.id, s.name])),
      domain_folder: new Map(folderList.map((f) => [f.id, f.name])),
    };
    return {
      options: { site: siteList, domain_folder: folderList } as Record<ResourceType, Option[]>,
      loading: { site: sites.isPending, domain_folder: folders.isPending } as Record<ResourceType, boolean>,
      failed: { site: sites.isError, domain_folder: folders.isError } as Record<ResourceType, boolean>,
      names,
      narrowable: narrowableMap(catalogue.data),
      descriptions: new Map((catalogue.data ?? []).map((p) => [p.key, p.description])),
    };
  }, [
    sites.data,
    sites.isPending,
    sites.isError,
    folders.data,
    folders.isPending,
    folders.isError,
    catalogue.data,
  ]);
}

export type AccessResources = ReturnType<typeof useAccessResources>;

interface Option {
  id: string;
  name: string;
}

const WORDS: Record<ResourceType, { all: string; some: string; one: string; many: string; none: string }> = {
  site: {
    all: "All websites",
    some: "Selected websites",
    one: "website",
    many: "websites",
    none: "This business has no websites yet.",
  },
  domain_folder: {
    all: "All domain folders",
    some: "Selected folders",
    one: "folder",
    many: "folders",
    none: "This business has no domain folders yet. Create one on the Domains page.",
  },
};

/**
 * One block per selected role that can be narrowed: "All websites" or "Selected websites", and/or
 * "All domain folders" or "Selected folders".
 */
export function RoleScopes({
  roles,
  draft,
  onChange,
  resources,
  disabled,
}: {
  roles: Role[];
  draft: ScopeDraft;
  onChange: (next: ScopeDraft) => void;
  resources: AccessResources;
  disabled?: boolean;
}) {
  const narrowable = roles
    .map((role) => ({ role, types: narrowableTypes(role, resources.narrowable) }))
    .filter((r) => r.types.length > 0);
  if (narrowable.length === 0) return null;

  const set = (roleId: string, type: ResourceType, ids: string[] | null) => {
    const current: RoleRestrictions = { ...(draft[roleId] ?? {}) };
    if (ids === null) delete current[type];
    else current[type] = ids;
    const next = { ...draft };
    if (Object.keys(current).length) next[roleId] = current;
    else delete next[roleId];
    onChange(next);
  };

  return (
    <div className="space-y-2">
      <p className="text-sm font-medium text-fg">
        Where it applies{" "}
        <span className="font-normal text-fg-subtle">
          The whole business, unless narrowed to specific websites or domain folders
        </span>
      </p>
      {narrowable.map(({ role, types }) => (
        <div key={role.id} className="space-y-3 rounded-xl border border-border px-4 py-3">
          {narrowable.length > 1 && <p className="text-sm font-semibold text-fg">{role.name}</p>}
          {types.map((type) => (
            <ScopeChoice
              key={type}
              roleName={role.name}
              type={type}
              selected={draft[role.id]?.[type] ?? null}
              options={resources.options[type]}
              loading={resources.loading[type]}
              failed={resources.failed[type]}
              disabled={disabled}
              onChange={(ids) => set(role.id, type, ids)}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

function ScopeChoice({
  roleName,
  type,
  selected,
  options,
  loading,
  failed,
  disabled,
  onChange,
}: {
  roleName: string;
  type: ResourceType;
  selected: string[] | null;
  options: Option[];
  loading: boolean;
  failed: boolean;
  disabled?: boolean;
  onChange: (ids: string[] | null) => void;
}) {
  const w = WORDS[type];
  const name = `scope-${roleName}-${type}`;
  // Ids no longer in the business (a deleted website) still show, so they can be unticked.
  const known = new Set(options.map((o) => o.id));
  const listed = [
    ...options,
    ...(selected ?? []).filter((id) => !known.has(id)).map((id) => ({ id, name: `Removed ${w.one}` })),
  ];
  return (
    <fieldset className="space-y-2" disabled={disabled}>
      <legend className="sr-only">
        {roleName}: {w.many}
      </legend>
      <div className="flex flex-wrap gap-x-5 gap-y-1.5 text-sm text-fg">
        <label className="inline-flex cursor-pointer items-center gap-2">
          <input
            type="radio"
            name={name}
            className="size-4 accent-brand-500"
            checked={selected === null}
            onChange={() => onChange(null)}
          />
          {w.all}
        </label>
        <label className="inline-flex cursor-pointer items-center gap-2">
          <input
            type="radio"
            name={name}
            className="size-4 accent-brand-500"
            checked={selected !== null}
            onChange={() => onChange(selected ?? [])}
          />
          {w.some}
        </label>
      </div>
      {selected !== null && (
        <div className="space-y-1.5">
          {loading ? (
            <Spinner className="size-4" />
          ) : failed ? (
            <p className="text-xs text-red-700">Couldn't load the {w.many}. Close this and try again.</p>
          ) : listed.length === 0 ? (
            <p className="text-xs text-fg-subtle">{w.none}</p>
          ) : (
            <ul
              aria-label={`${roleName}: ${w.some.toLowerCase()}`}
              className="max-h-40 divide-y divide-border overflow-y-auto rounded-[10px] border border-border"
            >
              {listed.map((o) => (
                <li key={o.id}>
                  <label className="flex cursor-pointer items-center gap-2.5 px-3 py-2 text-sm text-fg hover:bg-muted">
                    <input
                      type="checkbox"
                      className="size-4 accent-brand-500"
                      checked={selected.includes(o.id)}
                      onChange={(e) =>
                        onChange(
                          e.target.checked ? [...selected, o.id] : selected.filter((id) => id !== o.id),
                        )
                      }
                    />
                    <span className="min-w-0 truncate">{o.name}</span>
                  </label>
                </li>
              ))}
            </ul>
          )}
          {selected.length === 0 && listed.length > 0 && (
            <p className="text-xs text-[#995c05]">Choose at least one {w.one}.</p>
          )}
        </div>
      )}
    </fieldset>
  );
}

function useDebounced<T>(value: T, ms: number): T {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setSettled(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return settled;
}

/**
 * Identity's dry run of a role change, refreshed shortly after the choice changes: what someone
 * gains and loses, across the whole business and per website or folder.
 */
export function AccessPreviewPanel({
  userId,
  roleIds,
  restrictions,
  resources,
  who,
  invitation = false,
}: {
  /** Omit for an invitation. */
  userId?: string;
  roleIds: string[];
  restrictions: RestrictionsByRole;
  resources: AccessResources;
  /** "Riley", "You" or "They". */
  who: string;
  invitation?: boolean;
}) {
  const { orgId, current } = useOrganization();
  const body = useMemo(
    () => JSON.stringify({ user_id: userId, role_ids: roleIds, restrictions }),
    [userId, roleIds, restrictions],
  );
  const settled = useDebounced(body, 350);
  const incomplete = roleIds.length === 0 || emptySelections(restrictions);
  const preview = useQuery({
    queryKey: ["access-preview", orgId, settled],
    queryFn: () =>
      identity.previewAccess(orgId, JSON.parse(settled) as Parameters<typeof identity.previewAccess>[1]),
    // Only once the choice has settled, so a half-made choice is never sent.
    enabled: !incomplete && settled === body,
    placeholderData: keepPreviousData,
    retry: false,
    staleTime: 0,
  });
  const label = (p: string) => permissionLabel(p, resources.descriptions);
  const updating = settled !== body || preview.isFetching;

  let content;
  if (incomplete) {
    content = <p className="text-fg-subtle">Choose at least one website or folder to see the effect.</p>;
  } else if (preview.isError) {
    content = accessErrorMessage(preview.error, current.organization.name) ? (
      <Alert tone="danger">{accessErrorMessage(preview.error, current.organization.name)}</Alert>
    ) : (
      <ErrorAlert error={preview.error} />
    );
  } else if (!preview.data) {
    content = <p className="text-fg-subtle">Working out the effect…</p>;
  } else if (preview.data.added.length + preview.data.removed.length === 0) {
    content = <p className="text-fg-muted">No change to what {who === "You" ? "you" : who} can do.</p>;
  } else {
    content = (
      <>
        <ChangeList
          tone="gain"
          title={invitation ? `${who}'ll be able to` : `${who} ${who === "You" ? "gain" : "gains"}`}
          changes={preview.data.added}
          names={resources.names}
          label={label}
        />
        <ChangeList
          tone="loss"
          title={`${who} ${who === "You" ? "lose" : "loses"}`}
          changes={preview.data.removed}
          names={resources.names}
          label={label}
        />
      </>
    );
  }

  return (
    <section
      aria-label="Effective access"
      aria-busy={updating}
      className="space-y-2.5 rounded-xl border border-border bg-brand-50 px-4 py-3 text-sm"
    >
      <div className="flex items-center gap-2">
        <p className="text-xs font-semibold tracking-wide text-fg-subtle uppercase">
          {invitation ? "Access when they accept" : "Effective access after saving"}
        </p>
        {updating && !incomplete && <Spinner className="size-3.5" />}
      </div>
      {content}
      {!invitation && (
        <p className="text-xs text-fg-subtle">
          Saving applies the change straight away, and it's recorded in the{" "}
          <Link className="text-brand-700 hover:underline" to="/audit">
            audit log
          </Link>{" "}
          with your name.
        </p>
      )}
    </section>
  );
}

function ChangeList({
  tone,
  title,
  changes,
  names,
  label,
}: {
  tone: "gain" | "loss";
  title: string;
  changes: AccessChange[];
  names: ResourceNames;
  label: (p: string) => string;
}) {
  if (changes.length === 0) return null;
  const groups = groupChanges(changes, names, label);
  const Icon = tone === "gain" ? Plus : Minus;
  return (
    <div className="space-y-1.5">
      <p
        className={cx(
          "flex items-center gap-1.5 font-semibold",
          tone === "gain" ? "text-[#13734d]" : "text-red-700",
        )}
      >
        <Icon className="size-3.5" aria-hidden />
        {title}
      </p>
      <ul className="space-y-1 pl-5">
        {groups.map((g) => (
          <li key={g.key} className="text-fg">
            <span className="font-medium">{g.title}:</span>{" "}
            <span className="text-fg-muted">{g.permissions.map(lower).join(", ")}.</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

const lower = (s: string) => s.charAt(0).toLowerCase() + s.slice(1);

/** A refused role change in plain words (escalation, last Owner, invalid narrowing). */
export function AccessErrorAlert({ error }: { error: unknown }) {
  const { current } = useOrganization();
  if (!error) return null;
  const message = accessErrorMessage(error, current.organization.name);
  return message ? <Alert tone="danger">{message}</Alert> : <ErrorAlert error={error} />;
}
